import { Box, Collapse, Divider, Icon, Text, VStack } from "@chakra-ui/react";
import React, { useState } from "react";
import { HiChevronDown } from "react-icons/hi2";

import AiInterviewerSection from "./AiInterviewerSection";
import AiInterviewerSectionTitle from "./AiInterviewerSectionTitle";

export type NotificationView =
  | "notificationSettings"
  | "invitationEmail"
  | "invitationSms"
  | "reminder"
  | "completionEmail"
  | "completion";

export const NOTIFICATION_VIEW_SLUGS: Record<NotificationView, string> = {
  notificationSettings: "settings",
  invitationEmail: "invitation-email",
  invitationSms: "invitation-sms",
  reminder: "reminder",
  completionEmail: "completion-email",
  completion: "completion",
};

export const NOTIFICATION_VIEW_BY_SLUG: Record<string, NotificationView> =
  Object.fromEntries(
    Object.entries(NOTIFICATION_VIEW_SLUGS).map(([view, slug]) => [
      slug,
      view as NotificationView,
    ])
  );

type AiInterviewerNotificationsNavProps = {
  activeView: NotificationView;
  onActiveViewChange: (view: NotificationView) => void;
  isAtsLess: boolean;
  aiInterviewSmsEnabled: boolean;
  // Per-notification enabled state (shown as Enabled/Disabled badges)
  invitationEmailEnabled: boolean;
  invitationSmsEnabled: boolean;
  reminderEmailEnabled: boolean;
  completionEnabled: boolean;
};

type StatusBadgeProps = {
  enabled: boolean;
};

const StatusBadge: React.FC<StatusBadgeProps> = ({ enabled }) => (
  <Box
    px="6px"
    h="20px"
    display="flex"
    alignItems="center"
    borderRadius="3px"
    bg={enabled ? "rgba(0, 167, 140, 0.12)" : "gray.100"}
  >
    <Text
      fontSize="11.67px"
      fontWeight="semibold"
      lineHeight="16px"
      color={enabled ? "#177856" : "gray.700"}
    >
      {enabled ? "Enabled" : "Disabled"}
    </Text>
  </Box>
);

type NavigationButtonProps = {
  children: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
  endContent?: React.ReactNode;
  indented?: boolean;
};

const NavigationButton: React.FC<NavigationButtonProps> = ({
  children,
  isActive,
  onClick,
  endContent,
  indented = false,
}) => (
  <Box
    as="button"
    type="button"
    w="100%"
    py="10px"
    pl={indented ? 8 : 3}
    pr={3}
    textAlign="left"
    bg={isActive ? "blackAlpha.50" : "transparent"}
    color="gray.800"
    fontSize="sm"
    fontWeight="medium"
    borderRadius="8px"
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    _hover={{
      bg: isActive ? undefined : "blackAlpha.50",
    }}
    sx={{
      "&:focus, &:focus-visible": {
        outline: "none",
        boxShadow: "none",
      },
    }}
    onClick={onClick}
  >
    <Box flex="1">{children}</Box>
    {endContent}
  </Box>
);

export const AiInterviewerNotificationsNav: React.FC<
  AiInterviewerNotificationsNavProps
> = ({
  activeView,
  onActiveViewChange,
  isAtsLess,
  aiInterviewSmsEnabled,
  invitationEmailEnabled,
  invitationSmsEnabled,
  reminderEmailEnabled,
  completionEnabled,
}) => {
  const isInvitationActive =
    activeView === "invitationEmail" || activeView === "invitationSms";
  const [invitationExpanded, setInvitationExpanded] = useState(
    aiInterviewSmsEnabled && isInvitationActive
  );

  const handleInvitationHeaderClick = (): void => {
    if (aiInterviewSmsEnabled) {
      setInvitationExpanded((prev) => !prev);
    } else {
      onActiveViewChange("invitationEmail");
    }
  };

  return (
    <AiInterviewerSection w="376px" bg="gray.50">
      <AiInterviewerSectionTitle title="Candidate communications" />

      <VStack spacing={3} align="stretch" w="100%">
        {/* Top: Notification Settings (hidden in ATS-less mode) */}
        {!isAtsLess && (
          <>
            <NavigationButton
              isActive={activeView === "notificationSettings"}
              onClick={() => onActiveViewChange("notificationSettings")}
            >
              Notification Settings
            </NavigationButton>
            <Divider borderColor="gray.200" />
          </>
        )}

        {!isAtsLess && (
          <VStack spacing={0} align="stretch" w="100%">
            {/* Templates section heading */}
            <Box px={3} py="10px">
              <Text fontSize="sm" fontWeight="semibold" color="gray.400">
                Templates
              </Text>
            </Box>

            {/* Invitation (expandable when SMS enabled) */}
            <NavigationButton
              isActive={
                !aiInterviewSmsEnabled
                  ? activeView === "invitationEmail"
                  : isInvitationActive && !invitationExpanded
              }
              onClick={handleInvitationHeaderClick}
              endContent={
                aiInterviewSmsEnabled ? (
                  <Icon
                    as={HiChevronDown}
                    boxSize="5"
                    color="gray.500"
                    transform={
                      invitationExpanded ? "rotate(180deg)" : undefined
                    }
                    transition="transform 0.2s"
                  />
                ) : (
                  <StatusBadge enabled={invitationEmailEnabled} />
                )
              }
            >
              Invitation
            </NavigationButton>

            <Collapse in={invitationExpanded} animateOpacity>
              <VStack spacing={0} align="stretch">
                <NavigationButton
                  isActive={activeView === "invitationEmail"}
                  onClick={() => onActiveViewChange("invitationEmail")}
                  indented
                  endContent={<StatusBadge enabled={invitationEmailEnabled} />}
                >
                  Email
                </NavigationButton>
                <NavigationButton
                  isActive={activeView === "invitationSms"}
                  onClick={() => onActiveViewChange("invitationSms")}
                  indented
                  endContent={<StatusBadge enabled={invitationSmsEnabled} />}
                >
                  SMS
                </NavigationButton>
              </VStack>
            </Collapse>

            {/* Reminder */}
            <NavigationButton
              isActive={activeView === "reminder"}
              onClick={() => onActiveViewChange("reminder")}
              endContent={<StatusBadge enabled={reminderEmailEnabled} />}
            >
              Reminder
            </NavigationButton>

            {/* Completion */}
            <NavigationButton
              isActive={activeView === "completionEmail"}
              onClick={() => onActiveViewChange("completionEmail")}
              endContent={<StatusBadge enabled={completionEnabled} />}
            >
              Completion
            </NavigationButton>
          </VStack>
        )}
        {isAtsLess && (
          <NavigationButton
            isActive={activeView === "completion"}
            onClick={() => onActiveViewChange("completion")}
          >
            Completion notifications
          </NavigationButton>
        )}
      </VStack>

      {isAtsLess && (
        <Text fontSize="xs" color="gray.600" mt={4}>
          Candidates access interviews via the static shareable link. You share
          the link with them directly.
        </Text>
      )}
    </AiInterviewerSection>
  );
};

export default AiInterviewerNotificationsNav;
